/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ActionDispatcher,
  ConstantAction,
  constantAction,
  ContainerState,
  LogicalAction,
  logicalAction,
} from '@gh/common-shared';
import { TranslationParams, TranslationTuple } from '@gh/core-mls';
import { FormMode, NotificationService } from '@gh/core-ui';
import { isFunction, isString } from 'lodash';
import { CommonFlowState } from '../common-actions.service';

export interface FormFlowState<T> extends CommonFlowState<T> {
  form: ContainerState<T>;
  success?: boolean;
  trigger?: string;
  confirmation?: boolean;
}

export const isFormFlowStateSuccess = (fs: FormFlowState<any>) => fs.success || false;

const getMessage = <FS>(flowState: FS,
                        messageOrTuple?: string | TranslationTuple | TransformFn<FS, TranslationTuple>,
                        params?: TranslationParams): Maybe<TranslationTuple> => {
  if (isString(messageOrTuple)) {
    return { id: messageOrTuple, params };
  } else if (isFunction(messageOrTuple)) {
    return messageOrTuple(flowState);
  } else {
    return messageOrTuple;
  }
};

@Injectable()
export class FormActions {

  constructor(private notificationService: NotificationService) {

  }

  successNotification<FS extends FormFlowState<any>>(tuple: TransformFn<FS, TranslationTuple>): ConstantAction<FS>;
  successNotification<FS extends FormFlowState<any>>(tuple: TranslationTuple): ConstantAction<FS>;
  successNotification<FS extends FormFlowState<any>>(messageId?: string,
                                                     params?: TranslationParams): ConstantAction<FS>;
  successNotification<FS extends FormFlowState<any>>(messageOrTuple?: string | TranslationTuple | TransformFn<FS, TranslationTuple>,
                                                     params?: TranslationParams): ConstantAction<FS> {
    const actionName = 'successNotification.end';

    return constantAction(actionName, (dispatch: ActionDispatcher, flowState: FS) => {
      const message = getMessage(flowState, messageOrTuple, params) || {
        id: flowState.form.mode === FormMode.CreateMode ? 'msg_created_successfully' : 'msg_saved_successfully',
      };

      this.notificationService.success(message.id, message.params);

      dispatch(actionName, flowState);
    });
  }

  shouldSave<FS extends FormFlowState<any>>(): LogicalAction<FS> {
    const success = 'shouldSave.yes';
    const fail = 'shouldSave.no';

    return logicalAction(success, fail, (dispatch: ActionDispatcher, flowState: FS) => {
      dispatch(flowState.form.dirty ? success : fail, flowState);
    });
  }

}
